<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { AdditionalQuestion, QuestionType } from '@/types/guest';
import { AdditionalQuestionsService } from '@/api/guest/additionalQuestionsService';
import { GuestService } from '@/api/guest/guestService';

defineProps<{
  isVisible: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'question-added'): void;
  (e: 'question-deleted'): void;
}>();

const questionTemplates = ref<AdditionalQuestion[]>([]);
const newQuestion = ref<AdditionalQuestion>({ id: '', type: QuestionType.TEXT, answer: '' });
const isAddingQuestion = ref(false);

const questionTypes = [
  { label: 'Tekst', value: QuestionType.TEXT },
  { label: 'Tak/Nie', value: QuestionType.BOOLEAN },
  { label: 'Lista', value: QuestionType.ARRAY },
];

const loadQuestionTemplates = async () => {
  // Load existing templates
  const templates = AdditionalQuestionsService.getQuestionTemplates();

  // Load all guests to collect unique questions
  const guestsData = await GuestService.getGuestsData();

  // Collect all unique questions from guests
  const uniqueQuestions = new Set<string>();
  guestsData.forEach((guest) => {
    const guestQuestions =
      typeof guest['additionalQuestions'] === 'string'
        ? JSON.parse(guest['additionalQuestions'])
        : guest['additionalQuestions'] || [];

    guestQuestions.forEach((question: AdditionalQuestion) => {
      if (question.id) {
        uniqueQuestions.add(question.id);
      }
    });
  });

  // Combine templates with unique questions from guests
  const allQuestions = new Set([...templates.map((t) => t.id), ...uniqueQuestions]);
  questionTemplates.value = Array.from(allQuestions).map((id) => {
    const template = templates.find((t) => t.id === id);
    return (
      template || {
        id,
        type: QuestionType.TEXT,
        answer: '',
      }
    );
  });
};

const onAddQuestion = () => {
  isAddingQuestion.value = true;
  newQuestion.value = { id: '', type: QuestionType.TEXT, answer: '' };
};

const onSaveQuestion = () => {
  if (newQuestion.value.id) {
    AdditionalQuestionsService.addQuestionTemplate({
      id: newQuestion.value.id,
      type: newQuestion.value.type,
      answer: '',
    });
    loadQuestionTemplates();
    isAddingQuestion.value = false;
    newQuestion.value = { id: '', type: QuestionType.TEXT, answer: '' };
    emit('question-added');
  }
};

const onDeleteQuestion = async (questionId: string) => {
  try {
    // Remove question from templates
    AdditionalQuestionsService.removeQuestionTemplate(questionId);

    // Load all guests
    const guestsData = await GuestService.getGuestsData();

    // Remove question from all guests
    const updatedGuests = guestsData.map((guest) => {
      const guestQuestions =
        typeof guest['additionalQuestions'] === 'string'
          ? JSON.parse(guest['additionalQuestions'])
          : guest['additionalQuestions'] || [];

      return {
        id: String(guest['id']),
        name: String(guest['name']),
        surname: String(guest['surname']),
        presence: Boolean(guest['presence']),
        connection: String(guest['connection']),
        connectedGuestId: String(guest['connectedGuestId']),
        additionalQuestions: guestQuestions.filter((q: AdditionalQuestion) => q.id !== questionId),
      };
    });

    // Update guests data
    await GuestService.updateGuestsData({
      abortSignal: new AbortController().signal,
      data: updatedGuests,
    });

    // Reload templates
    loadQuestionTemplates();

    // Emit event to notify parent
    emit('question-deleted');
  } catch (error) {
    console.error('Failed to delete question:', error);
  }
};

const onClose = () => {
  emit('close');
};

onMounted(() => {
  loadQuestionTemplates();
});
</script>

<template>
  <div
    v-if="isVisible"
    class="question-templates-popup"
  >
    <div class="question-templates-popup__content">
      <div class="question-templates-popup__header">
        <h2>Zarządzanie pytaniami dodatkowymi</h2>
        <button
          class="question-templates-popup__close-button"
          @click="onClose"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>

      <div class="question-templates-popup__list">
        <div
          v-for="question in questionTemplates"
          :key="question.id"
          class="question-templates-popup__item"
        >
          <div class="question-templates-popup__item-content">
            <span>{{ question.id }}</span>
            <span class="question-templates-popup__item-type">{{ question.type }}</span>
          </div>
          <button
            class="question-templates-popup__delete-button"
            @click="() => onDeleteQuestion(question.id)"
          >
            <i class="pi pi-trash"></i>
          </button>
        </div>
      </div>

      <div
        v-if="isAddingQuestion"
        class="question-templates-popup__add-form"
      >
        <input
          v-model="newQuestion.id"
          type="text"
          placeholder="Wprowadź pytanie"
          class="question-templates-popup__input"
        />
        <Dropdown
          v-model="newQuestion.type"
          :options="questionTypes"
          option-label="label"
          option-value="value"
          placeholder="Wybierz typ"
          class="question-templates-popup__type-dropdown"
        />
        <button
          class="question-templates-popup__save-button"
          @click="onSaveQuestion"
        >
          Zapisz
        </button>
      </div>

      <button
        v-if="!isAddingQuestion"
        class="question-templates-popup__add-button"
        @click="onAddQuestion"
      >
        <i class="pi pi-plus"></i>
        Dodaj nowe pytanie
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.question-templates-popup {
  position: fixed;
  inset: 0;
  background-color: rgba($black, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  &__content {
    background-color: $white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }

  &__close-button {
    background: none;
    border: none;
    cursor: pointer;
    color: $brown-300;
    font-size: 1.5rem;
    padding: 0.5rem;

    &:hover {
      color: $brown-200;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background-color: $white-200;
    border-radius: 4px;
  }

  &__item-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
  }

  &__item-type {
    font-size: 0.9em;
    color: $brown-300;
    padding: 0.2rem 0.5rem;
    background-color: $brown-700;
    border-radius: 4px;
  }

  &__delete-button {
    background: none;
    border: none;
    cursor: pointer;
    color: #dc3545;
    padding: 0.5rem;

    &:hover {
      color: #c82333;
    }
  }

  &__add-form {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 1rem;
  }

  &__input {
    flex: 1;
    padding: 0.75rem;
    border: 1px solid $brown-300;
    border-radius: 4px;
    font-size: 1.5rem;
    height: 3rem;

    &:focus {
      outline: none;
      border-color: $brown-200;
    }
  }

  &__add-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 2rem;
    border: none;
    border-radius: 4px;
    background-color: $brown-200;
    color: $white;
    cursor: pointer;
    font-size: 1.6rem;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: $brown-100;
    }
  }

  &__save-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 4px;
    background-color: $brown-200;
    color: $white;
    cursor: pointer;
    font-size: 1.5rem;
    height: 3rem;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: $brown-100;
    }
  }

  &__type-dropdown {
    width: 150px;
  }
}
</style>
